/**
 * @fileoverview The top of a plant's, a bed's and a share link's public page, as the
 * garden page (gardenPageSections) has its own, so a visitor moving from one
 * to the next stays in the same Grow.
 *
 * - ItemHero: a Share button in the corner of a full-width photo scroller
 *   (the same swipeable strip the garden page's cards use: a small dot for each
 *   photo, the day it was taken over each), opened on the photo asked for.
 *   Grow's mark is the public top bar's only. On a wide screen each photo is
 *   drawn from its large copy (PhotoScroller sharp; D53), so it is not fuzzy
 *   on a computer; a phone keeps the card copy. With no photo it is the theme's
 *   green bar with the button, so the page never opens on a gap.
 * - ItemHeading: the name in the page's serif, a line under it (crop · bed ·
 *   grown by Amy) and the stage as a chip.
 * - ItemColumn: the page's column under the hero, with the garden page's
 *   spacing.
 *
 * The photo scroller, the words card, the plant grid and the invitation are the
 * garden page's own (gardenPageSections, gardenPageParts GardenInvite).
 *
 * Only what the backend sends is drawn, and it sends only public items and
 * photos checked safe (services/gardens/publicGarden, routes/shares).
 *
 * Where: GardenItemPage, SharePage.
 * API: none.
 * Settings: none of its own (PhotoScroller reads GARDEN_LARGE_PHOTO_MIN_WIDTH_PX).
 * Words (en/es): gardenPage.page.itemShareText, gardenPage.page.cardPhotos;
 * share.actions.*.
 *
 * @module itemPageSections
 */

import React from 'react';
import { cn } from '@/lib/utils';
import { Pill } from '@/shared/components/ui/Pill';
import { useTranslation } from '@/shared/hooks/useTranslation';
import type { GardenPhoto, GardenStage } from '@/shared/api/gardens';
import { CopyLinkButton, ShareError, ShareLinkButton } from './ShareButtons';
import { canShareLink } from './lib/shareLink';
import { GARDEN_COLUMN_CLASS, OVERLAY_BUTTON_CLASS, PhotoScroller } from './gardenPageSections';

/** What the hero is told: the photos, and the link its Share button gives. */
interface ItemHeroProps {
  /** The photos to scroll through, in order; none draws the green bar alone. */
  photos: GardenPhoto[];
  /** What the photo strip is, for a screen reader ("Photos of Cherokee Purple"). */
  label: string;
  /** The address the Share button shares. */
  link: string;
  /** The page's name, for the share menu's title. */
  title: string;
  /** The photo to show (from 0); the strip turns to it when it changes. */
  show?: number;
  /** A bed's: a collage of its photos first (PhotoScroller collage). */
  collage?: boolean;
  /**
   * The page's name and line (ItemHeading). Given, the page opens plainly
   * (owner, 2026-10-13: the photo header was confusing): the name with a small
   * Share beside it, then the photos in a rounded frame in the column, not
   * edge to edge under the top bar.
   */
  heading?: React.ReactNode;
}

/** The plain page's Share, beside the name: a quiet outline, as the garden page's. */
const SHARE_PLAIN_CLASS = 'h-8 rounded-full border bg-card px-3 text-sm text-foreground hover:bg-muted';

/**
 * The page's top: the Share (or Copy link) button in a corner over a light fade
 * on the photos, which scroll edge to edge below it. Grow's mark is the public
 * top bar's only (GardenFrame), not repeated over the photo. The strip is 4:3
 * on a phone and wider from a tablet up, never taller than the screen allows.
 * Without a photo, a green bar with the button.
 *
 * @param props - ItemHeroProps.
 */
export const ItemHero: React.FC<ItemHeroProps> = ({ photos, label, link, title, show, collage = false, heading }) => {
  const { reactTranslate: t } = useTranslation();
  const [error, setError] = React.useState<string | null>(null);
  if (heading) {
    return (
      <header className={cn(GARDEN_COLUMN_CLASS, 'space-y-4 pt-6')}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">{heading}</div>
          <div className="flex shrink-0 flex-col items-end gap-2 pt-1">
            {canShareLink()
              ? <ShareLinkButton link={link} title={title} text={t('gardenPage.page.itemShareText', { item: title })} onError={setError} className={SHARE_PLAIN_CLASS} />
              : <CopyLinkButton link={link} onError={setError} className={SHARE_PLAIN_CLASS} />}
            {error && <div className="max-w-xs rounded-md bg-card p-2"><ShareError text={error} /></div>}
          </div>
        </div>
        {/*
          * From a tablet up, a portrait frame of 18rem (about 300px), well
          * under the size the card copy is stored (about 450px; since D53 a
          * wide screen draws the large copy, about 1200px): drawn wider
          * they blurred, a landscape frame cropped the phone's portrait
          * photos, and at 400px it still filled the screen (owner,
          * 2026-10-14, twice: "still very large"). A phone keeps its
          * full-width frame.
          */}
        {photos.length > 0 && (
          <div className="mx-auto w-full overflow-hidden rounded-2xl border sm:max-w-[18rem]">
            <PhotoScroller
              photos={photos}
              label={label}
              aspect="aspect-[4/3] max-h-[70vh] sm:aspect-[4/5]"
              fallback={null}
              show={show}
              collage={collage}
              sharp
            />
          </div>
        )}
      </header>
    );
  }
  const bar = (
    <div className={cn(GARDEN_COLUMN_CLASS, 'flex items-start justify-end gap-3 py-3')}>
      <div className="pointer-events-auto flex flex-col items-end gap-2">
        {canShareLink()
          ? <ShareLinkButton link={link} title={title} text={t('gardenPage.page.itemShareText', { item: title })} onError={setError} className={OVERLAY_BUTTON_CLASS} />
          : <CopyLinkButton link={link} onError={setError} variant="default" className={OVERLAY_BUTTON_CLASS} />}
        {error && <div className="max-w-xs rounded-md bg-card/95 p-2"><ShareError text={error} /></div>}
      </div>
    </div>
  );
  if (photos.length === 0) return <header className="bg-primary">{bar}</header>;
  return (
    <header className="relative isolate bg-primary">
      <PhotoScroller
        photos={photos}
        label={label}
        aspect="aspect-[4/3] max-h-[75vh] sm:aspect-[16/9] lg:max-h-[520px]"
        fallback={null}
        show={show}
        collage={collage}
        sharp
      />
      {/* The fade lets the button read on any photo; neither it nor the bar catches a swipe. */}
      <div className="pointer-events-none absolute inset-x-0 top-0 z-20 h-16 bg-gradient-to-b from-black/35 to-transparent" aria-hidden="true" />
      <div className="pointer-events-none absolute inset-x-0 top-0 z-30">{bar}</div>
    </header>
  );
};

/** What the heading is told: the name, the line under it and the stage. */
interface ItemHeadingProps {
  /** The page's name: a plant's variety, a bed's name, a share's heading. */
  title: string;
  /** The line under the name; any node (it may hold links). */
  line?: React.ReactNode;
  /** The plant's stage now, as the backend sends it. */
  stage?: GardenStage | null;
  /** The stage as text already (a share photo's stage), when there is no stage object. */
  stageText?: string | null;
}

/**
 * The name, the line under it, and the stage as a chip: the garden page's plant
 * card at page size.
 *
 * @param props - ItemHeadingProps.
 */
export const ItemHeading: React.FC<ItemHeadingProps> = ({ title, line, stage, stageText }) => {
  // The stage's name is the server's, in the visitor's language.
  const chip = stage ? stage.name : stageText || null;
  return (
    <div>
      <h1 className="font-grow text-3xl font-bold leading-tight [overflow-wrap:anywhere] sm:text-4xl">{title}</h1>
      {line && <p className="mt-1 text-sm text-muted-foreground">{line}</p>}
      {chip && (
        <p className="mt-3">
          <Pill tone="primary">{chip}</Pill>
        </p>
      )}
    </div>
  );
};

/**
 * The column under the hero: centred, a phone's gutter either side, the garden
 * page's spacing between sections.
 *
 * @param props.children - The sections.
 * @param props.narrow - A reading width (a share page's), else the garden page's.
 */
export const ItemColumn: React.FC<{ children: React.ReactNode; narrow?: boolean }> = ({ children, narrow = false }) => (
  <div className={cn(GARDEN_COLUMN_CLASS, narrow && 'max-w-2xl', 'space-y-8 pb-12 pt-5')}>{children}</div>
);
